import { MeiliSearch } from "meilisearch";

class SearchService {
  constructor() {
    this.client = new MeiliSearch({
      host: "https://search.neemanfoundation.com",
      apiKey: "5yPSHRan3ELodI0MQ8SR", // Replace with your actual API key
    });
    this.index = this.client.index("neeman_organizations");
  }

  // Convert Algolia-style filters to Meilisearch format if needed
  convertFilter(filter) {
    if (!filter) return "";

    // Basic conversion - may need adjustment based on actual Meilisearch setup
    // Algolia: "country_code > 0 AND active:true"
    // Meilisearch: might need "country_code > 0 AND active = true"
    return filter.replace(/:/g, " = ");
  }

  async search(query = "", options = {}) {
    try {
      const { filter = "", limit = 24, offset = 0 } = options;

      const searchParams = {
        limit: limit * 2, // Request more results to account for filtering
        offset: 0, // Start from beginning when client-side filtering
      };

      // Handle empty query
      const searchQuery = query.trim() || "*";

      // Try to search with filters first
      const convertedFilter = this.convertFilter(filter);
      let results;
      let clientSideFilter = null;

      if (convertedFilter) {
        try {
          searchParams.filter = convertedFilter;
          results = await this.index.search(searchQuery, searchParams);
        } catch (filterError) {
          console.warn(
            "Filter not supported, falling back to client-side filtering:",
            filterError.message
          );
          // If filtering fails, search without filter and apply client-side filtering
          delete searchParams.filter;
          results = await this.index.search(searchQuery, searchParams);
          clientSideFilter = this.parseFilter(filter);
        }
      } else {
        results = await this.index.search(searchQuery, searchParams);
      }

      let hits = results.hits || [];

      // Apply client-side filtering if needed
      if (clientSideFilter) {
        hits = this.applyClientSideFilter(hits, clientSideFilter);
      }

      // Apply pagination after filtering
      const totalFilteredHits = hits.length;
      const startIndex = offset;
      const endIndex = startIndex + limit;
      hits = hits.slice(startIndex, endIndex);

      return {
        hits,
        totalHits: clientSideFilter
          ? totalFilteredHits
          : results.estimatedTotalHits || 0,
        currentPage: Math.floor(offset / limit),
        totalPages: Math.ceil(
          (clientSideFilter
            ? totalFilteredHits
            : results.estimatedTotalHits || 0) / limit
        ),
        isSearchStalled: false,
      };
    } catch (error) {
      console.error("Search error:", error);
      return {
        hits: [],
        totalHits: 0,
        currentPage: 0,
        totalPages: 0,
        isSearchStalled: false,
        error: error.message,
      };
    }
  }

  // Parse filter string into structured format for client-side filtering
  parseFilter(filterString) {
    if (!filterString) return null;

    const conditions = [];
    // Split by AND/OR (for now, just handle AND)
    const andParts = filterString.split(" AND ");

    for (const part of andParts) {
      const trimmed = part.trim();

      // Handle "field > value" format
      if (trimmed.includes(" > ")) {
        const [field, value] = trimmed.split(" > ");
        conditions.push({
          field: field.trim(),
          operator: ">",
          value: parseFloat(value.trim()),
        });
      }
      // Handle "field = value" format (converted from field:value)
      else if (trimmed.includes(" = ")) {
        const [field, value] = trimmed.split(" = ");
        conditions.push({
          field: field.trim(),
          operator: "=",
          value:
            value.trim() === "true"
              ? true
              : value.trim() === "false"
              ? false
              : value.trim(),
        });
      }
    }

    return conditions;
  }

  // Apply client-side filtering to results
  applyClientSideFilter(hits, conditions) {
    return hits.filter((hit) => {
      return conditions.every((condition) => {
        const fieldValue = hit[condition.field];

        switch (condition.operator) {
          case ">":
            return fieldValue > condition.value;
          case "=":
            return fieldValue === condition.value;
          case "<":
            return fieldValue < condition.value;
          default:
            return true;
        }
      });
    });
  }
}

export default new SearchService();
